import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const ITEMS_PER_PAGE = 12;

interface SearchParams {
  page: number;
  searchQuery: string;
  selectedCategories: string[];
  city: string;
  selectedLanguages: string[];
  entityType: 'all' | 'professionals' | 'companies';
  sortBy: string;
  userLocation: { lat: number; lng: number } | null;
  radius: number;
}

interface SearchResult {
  results: any[];
  total: number;
  totalPages: number;
  page: number;
}

async function fetchProfessionals(params: SearchParams): Promise<SearchResult> {
  const requestBody: Record<string, any> = {
    sort: params.sortBy,
    page: params.page,
    limit: ITEMS_PER_PAGE,
  };
  
  if (params.searchQuery) requestBody.q = params.searchQuery;
  if (params.selectedCategories.length > 0) requestBody.categories = params.selectedCategories;
  if (params.city) requestBody.region = params.city;
  if (params.selectedLanguages.length > 0) requestBody.languages = params.selectedLanguages;
  
  // Entity type filtering
  if (params.entityType === 'professionals') {
    requestBody.professionals_only = true;
  } else if (params.entityType === 'companies') {
    requestBody.companies_only = true;
  }
  
  if (params.userLocation) {
    requestBody.lat = params.userLocation.lat;
    requestBody.lng = params.userLocation.lng;
    requestBody.radius = params.radius;
  }

  // Get session in parallel with the request prep - but don't wait for it
  const sessionPromise = supabase.auth.getSession();
  
  // Start the request immediately
  const { data: { session } } = await sessionPromise;

  const { data, error } = await supabase.functions.invoke('search-professionals', {
    method: 'POST',
    body: requestBody,
    headers: session?.access_token ? {
      Authorization: `Bearer ${session.access_token}`,
    } : undefined,
  });

  if (error) throw error;

  return {
    results: data.results || [],
    total: data.total || 0,
    totalPages: data.totalPages || 0,
    page: data.page || 1,
  };
}

export function useProfessionalsSearch(params: SearchParams) {
  return useQuery({
    queryKey: [
      'professionals',
      params.page,
      params.searchQuery,
      params.selectedCategories,
      params.city,
      params.selectedLanguages,
      params.entityType,
      params.sortBy,
      params.userLocation?.lat,
      params.userLocation?.lng,
      params.radius,
    ],
    queryFn: () => fetchProfessionals(params),
    staleTime: 1000 * 60 * 5, // 5 minutes - data stays fresh longer
    gcTime: 1000 * 60 * 30, // 30 minutes in cache for faster back navigation
    placeholderData: (previousData) => previousData, // Keep showing old data while fetching new
  });
}

// Export fetch function for prefetching
export { fetchProfessionals };

export { ITEMS_PER_PAGE };
